import { useState } from 'react';
import { MapPin, Mail, Phone, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOTIVOS = [
  'Consulta general',
  'Solicitud de ingreso como profesional',
  'Afiliación de asociación',
  'Verificación de profesional',
  'Reporte de irregularidad',
  'Sugerencias o comentarios',
  'Otro'
];

export const ContactoPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    motivo: '',
    mensaje: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setIsSuccess(true);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      setError(err.response?.data?.detail || 'Error al enviar el mensaje. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-transition min-h-screen bg-[#0a0a0a]" data-testid="contacto-page">
      {/* Header */}
      <div className="bg-[#111111] border-b border-[#2a2a2a]">
        <div className="container-cni py-12 md:py-16 text-center">
          <span className="inline-block bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1 rounded-full text-sm font-medium mb-4">
            Comunícate con Nosotros
          </span>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Contacto
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            ¿Tienes dudas sobre el CNI? ¿Quieres formar parte del registro? 
            Estamos aquí para ayudarte.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container-cni py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6">
                <h3 className="font-manrope font-semibold text-lg text-white mb-4">
                  Información de Contacto
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Dirección</p>
                      <p className="text-sm text-gray-400">
                        Av. Paseo de la Reforma 250<br />
                        Col. Juárez, Cuauhtémoc<br />
                        06600 CDMX, México
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Correo Electrónico</p>
                      <p className="text-sm text-gray-400">contacto@cni.org.mx</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1a1a1a] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Teléfono</p>
                      <p className="text-sm text-gray-400">+52 55 1234 5678</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-6">
                <h3 className="font-manrope font-semibold text-lg text-white mb-2">
                  Horario de Atención
                </h3>
                <p className="text-gray-300 text-sm mb-4">
                  Lunes a Viernes<br />
                  9:00 AM - 6:00 PM
                </p>
                <p className="text-gray-500 text-xs">
                  Los mensajes recibidos fuera de horario serán atendidos el siguiente día hábil.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6 md:p-8">
                {isSuccess ? (
                  <div className="py-12 text-center" data-testid="contact-success">
                    <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="font-manrope font-semibold text-xl text-white mb-2">
                      Mensaje Enviado
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Gracias por contactarnos. Te responderemos a la brevedad posible.
                    </p>
                    <button 
                      onClick={() => {
                        setIsSuccess(false);
                        setFormData({ nombre: '', email: '', telefono: '', motivo: '', mensaje: '' });
                      }}
                      className="px-6 py-2 border border-[#2a2a2a] text-white rounded-lg hover:bg-[#1a1a1a] transition-colors"
                    >
                      Enviar otro mensaje
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-manrope font-semibold text-xl text-white mb-6">
                      Envíanos un Mensaje
                    </h2>
                    
                    {error && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="contact-error">
                        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-400">{error}</p>
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-1">
                            Nombre completo *
                          </label>
                          <input
                            id="nombre"
                            type="text"
                            required
                            value={formData.nombre}
                            onChange={(e) => handleChange('nombre', e.target.value)}
                            className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                            placeholder="Tu nombre"
                            data-testid="contact-nombre-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                            Correo electrónico *
                          </label>
                          <input
                            id="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                            placeholder="tu@email.com"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="telefono" className="block text-sm font-medium text-gray-300 mb-1">
                            Teléfono
                          </label>
                          <input
                            id="telefono"
                            type="tel"
                            value={formData.telefono}
                            onChange={(e) => handleChange('telefono', e.target.value)}
                            className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                            placeholder="+52 55 1234 5678"
                            data-testid="contact-telefono-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="motivo" className="block text-sm font-medium text-gray-300 mb-1">
                            Motivo de contacto *
                          </label>
                          <select
                            id="motivo"
                            required
                            value={formData.motivo}
                            onChange={(e) => handleChange('motivo', e.target.value)}
                            className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors appearance-none cursor-pointer"
                            data-testid="contact-motivo-select"
                            style={{
                              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                              backgroundPosition: 'right 0.75rem center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '1.5em 1.5em',
                              paddingRight: '2.5rem'
                            }}
                          >
                            <option value="" className="bg-[#1a1a1a] text-gray-500">Selecciona un motivo</option>
                            {MOTIVOS.map((motivo) => (
                              <option key={motivo} value={motivo} className="bg-[#1a1a1a] text-white">
                                {motivo}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="mensaje" className="block text-sm font-medium text-gray-300 mb-1">
                          Mensaje *
                        </label>
                        <textarea
                          id="mensaje"
                          required
                          value={formData.mensaje}
                          onChange={(e) => handleChange('mensaje', e.target.value)}
                          className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors min-h-[150px] resize-y"
                          placeholder="Describe tu consulta o solicitud..."
                          data-testid="contact-mensaje-input"
                        />
                      </div>

                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={isSubmitting || !formData.motivo}
                          className="inline-flex items-center justify-center px-8 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-colors"
                          data-testid="contact-submit-btn"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Enviar Mensaje
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
